import React, { KeyboardEventHandler, useState } from 'react';
import styles from './InstagramWideSearch.module.css';
import useInstagramCreatorProfiles from './useInstagramCreatorProfiles';
import useUrlState from '../../../Hooks/useUrlState';
import InstagramWideSearchTable from './InstagramWideSearchTable/InstagramWideSearchTable';
import {
    Button,
    Checkbox,
    DropdownOptionWithSubLabel,
    Label,
    OptionWithSubLabel,
    Select,
    Popover,
} from '@round/ui-kit';
import { ReactComponent as FilterIcon } from '../../../assets/FilterSearch.svg';
import { ReactComponent as DropdownIcon } from '../../../assets/ReactSelectDropdownArrow.svg';
import cn from 'classnames';
import { StylesConfig, ValueType } from 'react-select';
import { DebounceInput } from 'react-debounce-input';
import usePhylloLocationOptions from '../../Phyllo/hooks/usePhylloLocationOptions';
import useAbortableEffect from '../../../Hooks/useAbortableEffect';
import { getPhylloLocations, OrderingValues, PhylloInstagramCreatorsProfileSearchSortableFields } from '@round/api';
import capitalize from 'lodash/capitalize';
import { showNotification } from 'helpers';
import { GenericDropdownOption } from 'App.types';
import usePhylloInterests from '../../../Hooks/usePhylloInterests';
import GroupedOptionMultiSelect from '../../../ui/DataEntry/Select/GroupedSelectionsMultiSelect/GroupedSelectionsMultiSelect';
import PhylloAudienceLocationsMultiSelect, {
    PhylloLocationPercentageOption,
} from '../../Phyllo/WideSearch/components/AudienceLocationsSelect/PhylloAudienceLocationsSelect';
import { ParsedPhylloAudienceLocationsQueryParamItem } from '@round/api';
import { useCheckUserGroupsAccess } from '../../Auth/hooks/useCheckUserGroupsAccess';
import FollowerGrowthFilter from 'Modules/Phyllo/WideSearch/components/FollowerGrowthFilter/FollowerGrowthFilter';
import { sortingOptions } from 'Modules/Instagram/InstagramWideSearch/helpers';

type UrlState = {
    page: number;
    page_size: number;
} & Partial<{
    min_follower_count: number;
    max_follower_count: number;
    min_reels_views: number;
    max_reels_views: number;
    engagement_rate: number;
    bio_phrase: string;
    has_contact_details: boolean;
    location: string;
    audience_lookalikes: string;
    creator_lookalikes: string;
    creator_interests: string;
    audience_interests: string;
    min_audience_locations: string;
    hashtags: string;
    ordering: OrderingValues<PhylloInstagramCreatorsProfileSearchSortableFields>;
    follower_growth_interval: number;
    follower_growth_percentage_value: number;
}>;

const initialState: UrlState = {
    page: 1,
    page_size: 25,
    ordering: '-reels_views',
};

const DEBOUNCE_TIMEOUT = 700;

const selectStyles: StylesConfig = {
    control: (base) => ({
        ...base,
        borderColor: '#C2CFE0',
        minHeight: '2.5rem',
    }),
    valueContainer: (base) => ({
        ...base,
        flexWrap: 'nowrap',
    }),
};

const hashtagSelectStyles: StylesConfig = {
    ...selectStyles,
    ...{ valueContainer: (base) => ({ ...base, flexWrap: 'wrap' }) },
};

const hashtagSelectComponents = {
    DropdownIndicator: null,
    Menu: () => null,
    MenuList: () => null,
};

const InstagramWideSearch = () => {
    const [urlState, setUrlState] = useUrlState<UrlState>(initialState, { shouldSetMissingInitialValues: true });
    const [isFiltersBarVisible, setIsFiltersBarVisible] = useState(false);

    const isTrialUser = useCheckUserGroupsAccess(['wide_search_trial']);

    const page = urlState.page ? Number(urlState.page) : initialState.page;
    const pageSize = urlState.page_size ? Number(urlState.page_size) : initialState.page_size;
    const minFollowers = urlState.min_follower_count ? Number(urlState.min_follower_count) : undefined;
    const maxFollowers = urlState.max_follower_count ? Number(urlState.max_follower_count) : undefined;
    const minReelsViews = urlState.min_reels_views ? Number(urlState.min_reels_views) : undefined;
    const maxReelsViews = urlState.max_reels_views ? Number(urlState.max_reels_views) : undefined;
    const minEngagementRate = urlState.engagement_rate ? parseFloat(urlState.engagement_rate) : undefined;
    const followerGrowthPercentageValue = urlState.follower_growth_percentage_value
        ? Number(urlState.follower_growth_percentage_value)
        : undefined;
    const followerGrowthInterval = urlState.follower_growth_interval
        ? Number(urlState.follower_growth_interval)
        : undefined;

    const locationsSelectProps = usePhylloLocationOptions();
    const [selectedLocations, setSelectedLocations] = useState<ValueType<DropdownOptionWithSubLabel<string>, true>>([]);
    const [isLocationsFilterInitialized, setIsLocationFilterInitialized] = useState(false);

    const { options: audienceLocationOptions, ...audienceLocationSelectProps } = usePhylloLocationOptions();
    const audienceLocationOptionsWithPercentage: PhylloLocationPercentageOption[] = audienceLocationOptions.map(
        (option) => ({
            ...option,
            percentage: null,
        })
    );

    const [selectedAudienceLocations, setSelectedAudienceLocations] = useState<PhylloLocationPercentageOption[]>([]);
    const selectedAudienceLocationsWithPercentageValue = selectedAudienceLocations.filter((opt) => !!opt.percentage);

    const handleSetAudienceLocations = (options: PhylloLocationPercentageOption[]) => {
        const incomingOptionsWithPercentageValue = options.filter((opt) => !!opt.percentage);
        const currentOptionsWithPercentageValue = selectedAudienceLocations.filter((opt) => !!opt.percentage);

        const shouldDataReset = incomingOptionsWithPercentageValue.length !== currentOptionsWithPercentageValue.length;

        setSelectedAudienceLocations(options);
        setUrlState({
            min_audience_locations: !!options.length
                ? JSON.stringify(options.map((opt) => [opt.value, opt.percentage]))
                : undefined,
            ...(shouldDataReset ? { page: 1 } : {}),
        });

        if (shouldDataReset) {
            reset();
        }
    };

    const { interests, isLoading: isInterestsLoading } = usePhylloInterests();
    const interestsOptions: GenericDropdownOption<string>[] = interests.map((interest) => ({
        value: interest.name,
        label: interest.name,
    }));

    const selectedCreatorInterestsOptions = interestsOptions.filter(
        (interest) => urlState.creator_interests?.includes(interest.value)
    );

    const selectedAudienceInterestsOptions = interestsOptions.filter(
        (interest) => urlState.audience_interests?.includes(interest.value)
    );

    const { creators, creatorsCount, errorLoading, isLoading, isInitialized, reset } = useInstagramCreatorProfiles({
        page,
        page_size: pageSize,
        min_follower_count: minFollowers,
        max_follower_count: maxFollowers,
        min_reels_views: minReelsViews,
        max_reels_views: maxReelsViews,
        engagement_rate: minEngagementRate,
        bio_phrase: urlState.bio_phrase?.trim(),
        audience_lookalikes: urlState.audience_lookalikes?.trim(),
        creator_lookalikes: urlState.creator_lookalikes?.trim(),
        hashtags: urlState.hashtags?.trim(),
        creator_interests: selectedCreatorInterestsOptions.length
            ? JSON.stringify(selectedCreatorInterestsOptions.map((opt) => opt.value))
            : undefined,
        audience_interests: selectedAudienceInterestsOptions.length
            ? JSON.stringify(selectedAudienceInterestsOptions.map((opt) => opt.value))
            : undefined,
        has_contact_details: isTrialUser ? undefined : urlState.has_contact_details === 'true' || undefined,
        min_audience_locations: selectedAudienceLocationsWithPercentageValue.length
            ? JSON.stringify(
                  selectedAudienceLocationsWithPercentageValue.map((opt) => [
                      opt.value,
                      typeof opt.percentage === 'number' ? opt.percentage / 100 : null,
                  ])
              ).replace(/"/g, '')
            : undefined,
        location: selectedLocations?.map((loc) => loc.value).join(),
        ordering: urlState.ordering as OrderingValues<PhylloInstagramCreatorsProfileSearchSortableFields>,
        follower_growth_interval: followerGrowthInterval,
        follower_growth_percentage_value: followerGrowthPercentageValue
            ? followerGrowthPercentageValue / 100
            : undefined,
    });

    const selectedHashtags = urlState.hashtags?.split(',') ?? [];
    const [hashtagFilterRef, setHashtagFilterRef] = useState<HTMLButtonElement | null>(null);
    const [hashtagInputValue, setHashtagInputValue] = useState('');

    const handleHashtagKeyDown: KeyboardEventHandler = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            e.stopPropagation();

            if (selectedHashtags.includes(hashtagInputValue)) {
                return;
            }

            setUrlState({
                hashtags: [...selectedHashtags, hashtagInputValue].toString(),
                page: 1,
            });
            reset();
            setHashtagInputValue('');
        }
    };

    const selectedHashtagsLabel =
        selectedHashtags && selectedHashtags.length > 0
            ? `${selectedHashtags.length} hashtag${selectedHashtags.length > 1 ? 's' : ''} selected`
            : '';

    useAbortableEffect(
        (signal) => {
            const selectedLocationIds = selectedLocations?.map((loc) => loc.value) ?? [];
            const locationsToFetch = (urlState.location?.split(',').filter((item) => !!item) ?? []).filter(
                (loc) => !selectedLocationIds.includes(loc)
            );

            const partialAudienceLocationOptionsFromUrl: {
                value: string;
                percentage: number | null;
            }[] = urlState.min_audience_locations
                ? JSON.parse(urlState.min_audience_locations).map(
                      (el: ParsedPhylloAudienceLocationsQueryParamItem) => ({
                          value: el[0],
                          percentage: el[1],
                      })
                  )
                : [];

            const selectedAudienceLocationIds = selectedAudienceLocations.map((loc) => loc.value);
            const audienceLocationsToFetch = partialAudienceLocationOptionsFromUrl
                .map((loc) => loc.value)
                .filter((id) => !selectedAudienceLocationIds.includes(id));

            const allLocationsToFetch = locationsToFetch.concat(
                audienceLocationsToFetch.filter((id) => !locationsToFetch.includes(id))
            );

            if (allLocationsToFetch.length && !isLocationsFilterInitialized) {
                getPhylloLocations(
                    { uuid: allLocationsToFetch.join(), page_size: allLocationsToFetch.length },
                    { signal }
                )
                    .then((response) => {
                        setSelectedLocations(
                            response.data.results
                                .filter((item) => locationsToFetch.includes(item.uuid))
                                .map((item) => ({
                                    value: item.uuid,
                                    label: item.display_name,
                                    subLabel: capitalize(item.type),
                                }))
                        );

                        setSelectedAudienceLocations(
                            partialAudienceLocationOptionsFromUrl.map((opt) => {
                                const locationInResponse = response.data.results.find(
                                    (item) => item.uuid === opt.value
                                );
                                return {
                                    value: opt.value,
                                    label: locationInResponse?.display_name ?? opt.value,
                                    subLabel: capitalize(locationInResponse?.type ?? ''),
                                    percentage: opt.percentage,
                                };
                            })
                        );

                        setIsLocationFilterInitialized(true);
                    })
                    .catch((e) => {
                        if (e instanceof Error && e.name === 'AbortError') {
                            return;
                        }

                        showNotification('Could not initialize location filter', 'error');
                    });
            }
        },
        [
            isLocationsFilterInitialized,
            selectedLocations,
            selectedAudienceLocations,
            urlState.location,
            urlState.min_audience_locations,
        ]
    );

    const isTableLoading = isLoading || (!errorLoading && !isInitialized);
    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <h1 className={styles.title}>Instagram Influencer Wide Search</h1>

                <div className={styles.actions}>
                    <div className={styles.orderingSelectContainer}>
                        <Label className={styles.orderingSelectLabel} htmlFor="ordering">
                            Sort by
                        </Label>
                        <Select
                            className={styles.orderingSelect}
                            isSearchable={false}
                            options={sortingOptions}
                            value={sortingOptions.find((o) => o.value === urlState.ordering)}
                            onChange={(o) => {
                                setUrlState({ ordering: o?.value });
                                reset();
                            }}
                        />
                    </div>

                    <Button
                        className={styles.showFiltersButton}
                        type="bordered"
                        onClick={() => setIsFiltersBarVisible((visible) => !visible)}
                    >
                        <FilterIcon className={styles.showFiltersIcon} />
                        Filters
                    </Button>
                </div>
            </div>

            <div className={cn(styles.filters, { [styles.visible]: isFiltersBarVisible })}>
                <div className={styles.filtersRow}>
                    <div className={cn(styles.filter, styles.locationFilter)}>
                        <Label htmlFor="location" className={styles.filterLabel}>
                            Location
                        </Label>
                        <GroupedOptionMultiSelect
                            styles={selectStyles}
                            className={styles.filterSelect}
                            menuPortalTarget={document.body}
                            value={selectedLocations}
                            onChange={(value) => {
                                setSelectedLocations(value);
                                setUrlState({ location: value?.map((o) => o.value).join(','), page: 1 });
                                reset();
                            }}
                            components={{ Option: OptionWithSubLabel }}
                            placeholder="Search locations"
                            valueLabel={`${selectedLocations?.length} locations selected`}
                            {...locationsSelectProps}
                        />
                    </div>

                    <div className={styles.filter}>
                        <Label className={styles.filterLabel} htmlFor="audienceLocations">
                            Audience Locations
                        </Label>
                        <PhylloAudienceLocationsMultiSelect
                            value={selectedAudienceLocations}
                            options={audienceLocationOptionsWithPercentage}
                            onChange={handleSetAudienceLocations}
                            className={styles.hashtagFilter}
                            {...audienceLocationSelectProps}
                        />
                    </div>

                    <div className={styles.filter}>
                        <Label className={styles.filterLabel} htmlFor="bioPhrase">
                            Bio phrase
                        </Label>
                        <DebounceInput
                            className={styles.filterInput}
                            id="bioPhrase"
                            value={urlState.bio_phrase}
                            onChange={(e) => {
                                setUrlState({
                                    bio_phrase: e.target.value,
                                    page: 1,
                                });
                                reset();
                            }}
                            debounceTimeout={DEBOUNCE_TIMEOUT}
                            placeholder="Enter phrase..."
                        />
                    </div>

                    <div className={cn(styles.filterGroup, styles.pair)}>
                        <div className={styles.filter}>
                            <Label className={styles.filterLabel} htmlFor="audienceLookalikes">
                                Audience lookalike
                            </Label>
                            <DebounceInput
                                className={styles.filterInput}
                                id="audienceLookalikes"
                                value={urlState.audience_lookalikes}
                                onChange={(e) => {
                                    setUrlState({
                                        audience_lookalikes: e.target.value,
                                        page: 1,
                                    });
                                    reset();
                                }}
                                debounceTimeout={DEBOUNCE_TIMEOUT}
                                placeholder="Enter username..."
                            />
                        </div>

                        <div className={styles.filter}>
                            <Label className={styles.filterLabel} htmlFor="creatorLookalikes">
                                Creator lookalike
                            </Label>
                            <DebounceInput
                                className={styles.filterInput}
                                id="creatorLookalikes"
                                value={urlState.creator_lookalikes}
                                onChange={(e) => {
                                    setUrlState({
                                        creator_lookalikes: e.target.value,
                                        page: 1,
                                    });
                                    reset();
                                }}
                                debounceTimeout={DEBOUNCE_TIMEOUT}
                                placeholder="Enter username..."
                            />
                        </div>
                    </div>

                    {!isTrialUser && (
                        <div className={styles.filter}>
                            <Label className={styles.filterLabel} htmlFor="hasContactDetails">
                                Has contact details
                            </Label>
                            <div className={styles.checkboxInputContainer}>
                                <Checkbox
                                    className={styles.filterCheckboxInput}
                                    value={urlState.has_contact_details === 'true'}
                                    onChange={(hasContactDetails) => {
                                        setUrlState({ has_contact_details: hasContactDetails || undefined, page: 1 });
                                        reset();
                                    }}
                                />
                            </div>
                        </div>
                    )}
                </div>

                <div className={styles.filtersRow}>
                    <div className={cn(styles.filterGroup, styles.pair)}>
                        <div className={styles.filter}>
                            <Label className={styles.filterLabel} htmlFor="creator_interests">
                                Creator Interests
                            </Label>
                            <GroupedOptionMultiSelect
                                styles={selectStyles}
                                className={styles.filterSelect}
                                menuPortalTarget={document.body}
                                value={selectedCreatorInterestsOptions}
                                options={interestsOptions}
                                onChange={(value) => {
                                    setUrlState({
                                        creator_interests: value?.map((o) => o.value).join(','),
                                        page: 1,
                                    });
                                    reset();
                                }}
                                isLoading={isInterestsLoading}
                                placeholder="Search interests"
                                valueLabel={`${selectedCreatorInterestsOptions.length} interests selected`}
                            />
                        </div>

                        <div className={styles.filter}>
                            <Label className={styles.filterLabel} htmlFor="audience_interests">
                                Audience Interests
                            </Label>
                            <GroupedOptionMultiSelect
                                styles={selectStyles}
                                className={styles.filterSelect}
                                menuPortalTarget={document.body}
                                value={selectedAudienceInterestsOptions}
                                options={interestsOptions}
                                onChange={(value) => {
                                    setUrlState({
                                        audience_interests: value?.map((o) => o.value).join(','),
                                        page: 1,
                                    });
                                    reset();
                                }}
                                isLoading={isInterestsLoading}
                                placeholder="Search interests"
                            />
                        </div>
                    </div>

                    <div className={styles.filter}>
                        <Label className={styles.filterLabel} htmlFor="hashtags">
                            Hashtags
                        </Label>
                        <>
                            <button ref={setHashtagFilterRef} className={styles.hashtagFilter}>
                                {selectedHashtags && selectedHashtags?.length > 0 ? (
                                    <span>{selectedHashtagsLabel}</span>
                                ) : (
                                    <span className={styles.placeholder}>Select hashtags...</span>
                                )}
                                <DropdownIcon />
                            </button>
                            <Popover
                                anchorElement={hashtagFilterRef}
                                showOn="click"
                                options={{
                                    placement: 'bottom-start',
                                    modifiers: [
                                        { name: 'offset', options: { offset: [0, 8] } },
                                        { name: 'recalcDependency', options: selectedHashtags },
                                    ],
                                }}
                            >
                                <div className={styles.hashtagDropdown}>
                                    <Select
                                        value={selectedHashtags.map((hashtag) => ({
                                            value: hashtag,
                                            label: hashtag,
                                        }))}
                                        onChange={(options: ValueType<GenericDropdownOption<string>, true>) => {
                                            setUrlState({
                                                hashtags: options?.length
                                                    ? options.map((opt) => opt.value).toString()
                                                    : undefined,
                                                page: 1,
                                            });
                                            reset();
                                        }}
                                        inputValue={hashtagInputValue}
                                        onInputChange={(value) => setHashtagInputValue(value)}
                                        onKeyDown={handleHashtagKeyDown}
                                        menuPortalTarget={null}
                                        isClearable
                                        isMulti
                                        autoFocus
                                        filterOption={null}
                                        placeholder="Type a hashtag and press Enter"
                                        components={hashtagSelectComponents}
                                        styles={hashtagSelectStyles}
                                    />
                                </div>
                            </Popover>
                        </>
                    </div>
                </div>

                <div className={styles.filtersRow}>
                    <div className={cn(styles.filterGroup, styles.pair)}>
                        <div className={styles.filter}>
                            <Label className={styles.filterLabel} htmlFor="minFollowers">
                                Min followers
                            </Label>
                            <DebounceInput
                                className={styles.filterInput}
                                id="minFollowers"
                                type="number"
                                value={minFollowers}
                                onChange={(e) => {
                                    setUrlState({
                                        min_follower_count: e.target.value ? parseInt(e.target.value) : undefined,
                                        page: 1,
                                    });
                                    reset();
                                }}
                                debounceTimeout={DEBOUNCE_TIMEOUT}
                            />
                        </div>

                        <div className={styles.filter}>
                            <Label className={styles.filterLabel} htmlFor="maxFollowers">
                                Max followers
                            </Label>
                            <DebounceInput
                                className={styles.filterInput}
                                id="maxFollowers"
                                type="number"
                                value={maxFollowers}
                                onChange={(e) => {
                                    setUrlState({
                                        max_follower_count: e.target.value ? parseInt(e.target.value) : undefined,
                                        page: 1,
                                    });
                                    reset();
                                }}
                                debounceTimeout={DEBOUNCE_TIMEOUT}
                            />
                        </div>
                    </div>

                    <div className={cn(styles.filterGroup, styles.pair)}>
                        <div className={styles.filter}>
                            <Label className={styles.filterLabel} htmlFor="minReelsViews">
                                Min avg. reels views
                            </Label>
                            <DebounceInput
                                className={styles.filterInput}
                                id="minReelsViews"
                                type="number"
                                value={minReelsViews}
                                onChange={(e) => {
                                    setUrlState({
                                        min_reels_views: e.target.value ? parseInt(e.target.value) : undefined,
                                        page: 1,
                                    });
                                    reset();
                                }}
                                debounceTimeout={DEBOUNCE_TIMEOUT}
                            />
                        </div>

                        <div className={styles.filter}>
                            <Label className={styles.filterLabel} htmlFor="maxReelsViews">
                                Max avg. reels views
                            </Label>
                            <DebounceInput
                                className={styles.filterInput}
                                id="maxReelsViews"
                                type="number"
                                value={maxReelsViews}
                                onChange={(e) => {
                                    setUrlState({
                                        max_reels_views: e.target.value ? parseInt(e.target.value) : undefined,
                                        page: 1,
                                    });
                                    reset();
                                }}
                                debounceTimeout={DEBOUNCE_TIMEOUT}
                            />
                        </div>
                    </div>

                    <div className={styles.filter}>
                        <Label className={styles.filterLabel} htmlFor="engagementRate">
                            Min engagement rate
                        </Label>
                        <DebounceInput
                            className={styles.filterInput}
                            id="engagementRate"
                            type="number"
                            inputMode="decimal"
                            value={minEngagementRate}
                            onChange={(e) => {
                                setUrlState({
                                    engagement_rate: e.target.value ? parseFloat(e.target.value) : undefined,
                                    page: 1,
                                });
                                reset();
                            }}
                            debounceTimeout={DEBOUNCE_TIMEOUT}
                        />
                    </div>

                    <div className={styles.filter}>
                        <Label className={styles.filterLabel} htmlFor="followerGrowth">
                            Min follower growth
                        </Label>
                        <FollowerGrowthFilter
                            value={{ interval: followerGrowthInterval, percentage: followerGrowthPercentageValue }}
                            onChange={({ percentage, interval }) => {
                                setUrlState({
                                    follower_growth_interval: interval,
                                    follower_growth_percentage_value: percentage,
                                    page: 1,
                                });
                                reset();
                            }}
                        />
                    </div>
                </div>
            </div>

            <InstagramWideSearchTable
                loading={isTableLoading}
                data={creators}
                count={creatorsCount}
                page={page}
                setPage={(page) => setUrlState({ page })}
                pageSize={pageSize}
                setPageSize={(pageSize) => setUrlState({ page_size: pageSize ?? initialState.page_size })}
                noDataLabel={errorLoading ? "Sorry, we're experiencing technical issues" : 'No data found'}
            />
        </div>
    );
};

export default InstagramWideSearch;
